import type { Activity } from '../api/types';
import { climb, length } from './activities';
import { units, usesMiles } from '../stores/units.svelte';
import { DURATION_STEPS, decimals, durationLabel, niceTicks, type Scale } from './chart';
import { sportOf } from './sports';
import { weekStart } from './progress';
import { integer, locale, t } from '../i18n/index.ts';

// Wording and data for the Activities screen's sections (spec 006), the
// same as the apps' shared Insights, from i18n/common (spec 013 FR-010).

export type GoalMeasure = 'distance' | 'time' | 'elevation' | 'count' | 'steps' | 'active_minutes';
export type GoalPeriodKind = 'day' | 'week' | 'month' | 'year';

// Weeks is a sport's totals for the 13 weeks ending with the current one.
export interface Weeks {
  sport: string;
  weeks: string[];
  distance_m: number[];
  moving_s: number[];
  elevation_m: number[];
  count: number[];
}

export interface Goal {
  id: string;
  sport: string;
  measure: GoalMeasure;
  period: GoalPeriodKind;
  target: number;
  period_start: string;
  period_end: string;
  done: number;
  met: boolean;
}

export interface GoalPeriod {
  period_start: string;
  period_end: string;
  done: number;
  target: number;
  met: boolean;
}

export type TileId = 'steps' | 'active_minutes' | 'distance' | 'time' | 'elevation' | 'calories' | 'count' | 'exercise_days';

export interface Tiles {
  tiles: TileId[];
  week: string[];
  days: {
    distance_m: number[];
    moving_s: number[];
    elevation_m: number[];
    calories: number[];
    count: number[];
    active_minutes: number[];
  };
  steps: { days: string[]; values: number[]; any: boolean };
  active_target: number;
}

export interface LogWeek {
  week: string;
  days: { day: string; moving_s: number; activities: Activity[] }[];
}

export interface MonthData {
  month: string;
  running: number[];
  prior_running: number[];
  count: number;
  prior_same_day: number;
  recap: {
    count: number;
    moving_s: number;
    distance_m: number;
    days_active: number;
    top_sport?: string;
    longest?: Activity;
  };
}

export interface Health {
  steps_today: number;
  steps_any: boolean;
  active_minutes_week: number;
}

// calendar is the query every section sends: the browser's zone and its
// locale's first day of the week.
export function calendar(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `tz=${encodeURIComponent(tz)}&week_start=${weekStart(navigator.language)}`;
}

// hours is "1h 55m" or "42m", as This week and the tiles write time.
export function hours(seconds: number): string {
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? t('durationCompact', { hours: h, minutes: m % 60 }) : t('durationCompactMinutes', { minutes: m });
}

// count writes a whole number with the region's grouping, "3,091".
export function count(n: number): string {
  return integer(Math.round(n));
}

// named is a record of each key's words, read from the catalog each time so
// they follow the language.
function named<K extends string>(keys: readonly K[], words: (k: K) => string): Record<K, string> {
  return Object.defineProperties({} as Record<K, string>, Object.fromEntries(keys.map((k) => [k, { enumerable: true, get: () => words(k) }])));
}

export const MEASURE_NAMES: Record<GoalMeasure, string> = named(
  ['distance', 'time', 'elevation', 'count', 'steps', 'active_minutes'] as const,
  (measure) => t('insightsMeasure', { measure }),
);

export const PERIOD_NAMES: Record<GoalPeriodKind, string> = named(['day', 'week', 'month', 'year'] as const, (period) => t('insightsPeriod', { period }));

// periodNow is how a goal's current period reads.
export function periodNow(p: GoalPeriodKind): string {
  return t('insightsPeriodNow', { period: p });
}

// measureValue writes an amount of a measure in the viewer's units.
export function measureValue(m: GoalMeasure, v: number): string {
  switch (m) {
    case 'distance': return length(v);
    case 'time': return hours(v);
    case 'elevation': return climb(v);
    case 'active_minutes': return t('durationMinutes', { minutes: Math.round(v) });
    case 'steps': return count(v);
  }
  return count(v);
}

// MeasureAxis is a chart's value scale for a measure (spec 010 FR-033):
// factor turns a stored amount into the viewer's unit, and tick writes
// each of the scale's values without the unit, which heads the scale.
export interface MeasureAxis {
  factor: number;
  unit: string;
  scale: (max: number) => Scale;
  tick: (v: number, step: number) => string;
}

// measureAxis is the scale for charting a measure or calories from 0.
export function measureAxis(m: GoalMeasure | 'calories'): MeasureAxis {
  const axisUnit = (unit: string) => t('insightsAxisUnit', { unit });
  const whole = (unit: string, factor = 1): MeasureAxis => ({
    factor, unit: axisUnit(unit), scale: (max) => niceTicks(0, max, { zero: true, whole: true }), tick: (v) => count(v),
  });
  switch (m) {
    case 'distance': {
      const factor = 1 / (usesMiles() ? 1609.344 : 1000);
      return { factor, unit: axisUnit(usesMiles() ? 'mi' : 'km'), scale: (max) => niceTicks(0, max, { zero: true }), tick: (v, step) => v.toLocaleString(locale(), { minimumFractionDigits: decimals(step), maximumFractionDigits: decimals(step) }) };
    }
    case 'time': return { factor: 1, unit: '', scale: (max) => niceTicks(0, max, { zero: true, steps: DURATION_STEPS.filter((s) => s >= 60) }), tick: (v) => (v ? durationLabel(v) : '0') };
    case 'elevation': return whole(units().elevation === 'ft' ? 'ft' : 'm', units().elevation === 'ft' ? 3.28084 : 1);
    case 'active_minutes': return whole('min');
    case 'steps': return whole('steps');
    case 'calories': return whole('kcal');
  }
  return whole('activities');
}

// goalScope is what a goal counts: "All Run", "All sports", or "Steps".
export function goalScope(g: Pick<Goal, 'sport' | 'measure'>): string {
  if (g.measure === 'steps' || g.measure === 'active_minutes') return MEASURE_NAMES[g.measure];
  return g.sport ? t('insightsAllSport', { sport: sportOf(g.sport).name }) : t('insightsAllSports');
}

// goalLeft says how much is left, or that the goal is met and by how much.
export function goalLeft(g: Pick<Goal, 'measure' | 'done' | 'target'>): string {
  if (g.done >= g.target) {
    const over = g.done - g.target;
    return over > 0 ? t('insightsGoalMetOver', { amount: measureValue(g.measure, over) }) : t('insightsGoalMet');
  }
  return t('insightsToGo', { amount: measureValue(g.measure, g.target - g.done) });
}

// goalMeasures are the measures a goal of a sport may have: a sport without
// a map has no distance or elevation, and steps and active minutes have no
// sport.
export function goalMeasures(sport: string): GoalMeasure[] {
  if (!sport) return ['distance', 'time', 'elevation', 'count', 'steps', 'active_minutes'];
  return sportOf(sport).gps ? ['distance', 'time', 'elevation', 'count'] : ['time', 'count'];
}

// goalPeriods are the periods a measure may have; only steps are daily.
export function goalPeriods(m: GoalMeasure): GoalPeriodKind[] {
  return m === 'steps' ? ['day', 'week', 'month', 'year'] : ['week', 'month', 'year'];
}

// targetUnit is the unit a target is typed in, and toStored and fromStored
// convert between it and the stored meters or seconds.
export function targetUnit(m: GoalMeasure): string {
  return t('insightsTargetUnit', { unit: targetUnitCode(m) });
}

function targetUnitCode(m: GoalMeasure): string {
  switch (m) {
    case 'distance': return usesMiles() ? 'mi' : 'km';
    case 'time': return 'hours';
    case 'elevation': return units().elevation === 'ft' ? 'ft' : 'm';
    case 'active_minutes': return 'minutes';
    case 'steps': return 'steps';
  }
  return 'activities';
}

export function toStored(m: GoalMeasure, typed: number): number {
  switch (m) {
    case 'distance': return typed * (usesMiles() ? 1609.344 : 1000);
    case 'time': return typed * 3600;
    case 'elevation': return units().elevation === 'ft' ? typed / 3.28084 : typed;
  }
  return typed;
}

export function fromStored(m: GoalMeasure, stored: number): number {
  const round = (v: number) => Math.round(v * 10) / 10;
  switch (m) {
    case 'distance': return round(stored / (usesMiles() ? 1609.344 : 1000));
    case 'time': return round(stored / 3600);
    case 'elevation': return Math.round(units().elevation === 'ft' ? stored * 3.28084 : stored);
  }
  return stored;
}

// TILES are every tile in the order the editor offers them.
export const TILES: { id: TileId; name: string }[] = (
  ['steps', 'active_minutes', 'distance', 'time', 'elevation', 'calories', 'count', 'exercise_days'] as const
).map((id) => ({
  id,
  get name() {
    return tileName(id);
  },
}));

export function tileName(id: TileId): string {
  return t('insightsTile', { tile: id });
}

// TileView is a tile's value, its caption, its 7 bars with their days and
// how a day's bar reads, and its badge.
export interface TileView {
  value: string;
  caption: string;
  bars: number[];
  labels: string[];
  days: string[];
  format: (v: number) => string;
  badge?: { text: string; met: boolean };
  noData: boolean;
}

const tileMeasure: Partial<Record<TileId, GoalMeasure>> = {
  steps: 'steps', active_minutes: 'active_minutes', distance: 'distance', time: 'time', elevation: 'elevation', count: 'count',
};

// dayLetter is a day's first letter in the viewer's language, "M".
export function dayLetter(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(locale(), { weekday: 'narrow' });
}

// tileView works out what a tile shows (006 FR-079): steps are today's with the
// last 7 days; the rest are this week's with each day of it. A goal of the
// same measure over all sports gives the badge: the daily steps goal for
// steps, the weekly goal for the rest, and the weekly active minutes target
// for active minutes.
export function tileView(id: TileId, tiles: Tiles, goals: Goal[]): TileView {
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  const d = tiles.days;
  const week = tiles.week.map(dayLetter);
  let v: TileView;
  const base = { labels: week, days: tiles.week, noData: false };
  switch (id) {
    case 'steps': {
      const today = tiles.steps.values[tiles.steps.values.length - 1] ?? 0;
      v = { value: count(today), caption: t('insightsToday'), bars: tiles.steps.values, labels: tiles.steps.days.map(dayLetter), days: tiles.steps.days, format: (n) => t('insightsSteps', { n: Math.round(n) }), noData: !tiles.steps.any };
      break;
    }
    case 'active_minutes':
      v = { ...base, value: `${count(sum(d.active_minutes))}`, caption: t('insightsOfThisWeek', { target: count(tiles.active_target) }), bars: d.active_minutes, format: (n) => t('durationMinutes', { minutes: Math.round(n) }) };
      break;
    case 'distance':
      v = { ...base, value: length(sum(d.distance_m)), caption: t('insightsThisWeek'), bars: d.distance_m, format: length };
      break;
    case 'time':
      v = { ...base, value: hours(sum(d.moving_s)), caption: t('insightsThisWeek'), bars: d.moving_s, format: hours };
      break;
    case 'elevation':
      v = { ...base, value: climb(sum(d.elevation_m)), caption: t('insightsThisWeek'), bars: d.elevation_m, format: climb };
      break;
    case 'calories':
      v = { ...base, value: t('unitsKcal', { calories: count(sum(d.calories)) }), caption: t('insightsThisWeek'), bars: d.calories, format: (n) => t('unitsKcal', { calories: count(n) }) };
      break;
    case 'count':
      v = { ...base, value: count(sum(d.count)), caption: t('insightsThisWeek'), bars: d.count, format: (n) => t('insightsActivities', { n: Math.round(n) }) };
      break;
    case 'exercise_days':
      v = { ...base, value: t('insightsDaysOfWeek', { days: d.count.filter((n) => n > 0).length }), caption: t('insightsDaysThisWeek'), bars: d.count.map((n) => (n > 0 ? 1 : 0)), format: (n) => (n > 0 ? t('insightsExercised') : t('insightsNoExercise')) };
      break;
  }
  const m = tileMeasure[id];
  if (m && !v.noData) {
    const period: GoalPeriodKind = m === 'steps' ? 'day' : 'week';
    const g = goals.find((x) => x.measure === m && x.period === period && !x.sport);
    if (g) {
      v.badge = { text: g.met ? t('insightsGoalMet') : t('insightsToGo', { amount: measureValue(m, g.target - g.done) }), met: g.met };
    } else if (m === 'active_minutes') {
      const done = sum(d.active_minutes);
      v.badge = { text: done >= tiles.active_target ? t('insightsGoalMet') : t('insightsToGo', { amount: count(tiles.active_target - done) }), met: done >= tiles.active_target };
    }
  }
  return v;
}

// monthSentence compares this month so far with last month by the same day
// (006 FR-071).
export function monthSentence(m: MonthData): string {
  const prior = integer(m.prior_same_day);
  if (m.count > m.prior_same_day) return t('insightsMonthUp', { count: m.count, prior });
  if (m.count < m.prior_same_day) return t('insightsMonthDown', { count: m.count, prior });
  return t('insightsMonthSame', { count: m.count });
}

// monthName is "September 2026" for "2026-09".
export function monthName(month: string): string {
  return new Date(`${month}-15T12:00:00`).toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
}

// monthBefore is the month before "2026-09", "2026-08".
export function monthBefore(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// monthAfter is the month after "2026-08", "2026-09".
export function monthAfter(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// weekRange is "Sep 21 – 27" for a week's first day, as the region writes a
// range. Thin and narrow spaces become plain ones, as times do.
export function weekRange(first: string): string {
  const a = new Date(`${first}T12:00:00`);
  const b = new Date(a);
  b.setDate(a.getDate() + 6);
  return new Intl.DateTimeFormat(locale(), { month: 'short', day: 'numeric' }).formatRange(a, b).replace(/[  ]/g, ' ');
}

// shiftDay moves a YYYY-MM-DD day by n days.
export function shiftDay(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// today is the viewer's day, YYYY-MM-DD.
export function today(): string {
  return shiftDay(new Date().toLocaleDateString('en-CA'), 0);
}

